import { useState, useEffect, useMemo } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { TrophyIcon, ChartBarIcon } from '@heroicons/react/24/outline';

// Staff self-view of their own goal scorecard — read-only. Reuses the existing
// GET /api/goals/staff/:staffId/matrix (canViewStaff allows a user to load their
// OWN matrix); scoring stays management-only. Mirrors the director scorecard grid
// without any editing.

const ROLE_LABEL = { director: 'Director', admin: 'Office Admin', teacher: 'Teacher', float: 'Float', cook: 'Cook' };
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function periodsForTemplate(t, today = new Date()) {
  if (t.measurement_type === 'review_periods') {
    const labels = Array.isArray(t.period_labels)
      ? t.period_labels
      : (t.period_labels ? JSON.parse(t.period_labels) : []);
    return labels.map((l) => ({ label: l, short: l }));
  }
  if (t.measurement_type === 'annual' || t.measurement_type === 'one_time') {
    const y = today.getFullYear();
    return [{ label: String(y), short: String(y) }];
  }
  if (t.measurement_type === 'quarterly') {
    const out = [];
    let y = today.getFullYear();
    let q = Math.floor(today.getMonth() / 3) + 1;
    for (let i = 0; i < 4; i++) {
      out.unshift({ label: `${y}-Q${q}`, short: `Q${q} ${String(y).slice(2)}` });
      q -= 1;
      if (q === 0) { q = 4; y -= 1; }
    }
    return out;
  }
  const out = [];
  const y0 = today.getFullYear();
  const m0 = today.getMonth();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(y0, m0 - i, 1);
    out.push({ label: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, short: `${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}` });
  }
  return out;
}

function scoreColor(score) {
  if (score == null) return 'bg-gray-50 text-gray-400 border-gray-200';
  const n = Number(score);
  if (n >= 4.5) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (n >= 3.5) return 'bg-lime-50 text-lime-700 border-lime-200';
  if (n >= 2.5) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-red-50 text-red-700 border-red-200';
}

function CategoryBlock({ cat }) {
  const groups = useMemo(() => {
    const map = new Map();
    for (const t of cat.templates) {
      const periods = periodsForTemplate(t);
      const key = periods.map((p) => p.label).join('|');
      if (!map.has(key)) map.set(key, { periods, templates: [] });
      map.get(key).templates.push(t);
    }
    return Array.from(map.values());
  }, [cat]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{cat.name}</h3>
      </div>
      {groups.map((g, gi) => (
        <div key={gi} className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide w-1/3">Goal</th>
                {g.periods.map((p) => (
                  <th key={p.label} className="text-center px-2 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{p.short}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {g.templates.map((t) => (
                <tr key={t.id} className="border-b border-gray-50 last:border-b-0">
                  <td className="px-4 py-3 align-top">
                    <p className="font-medium text-gray-900 leading-snug">{t.title}</p>
                    {t.rubric && <p className="text-xs text-gray-500 mt-1 leading-snug whitespace-pre-line">{t.rubric}</p>}
                  </td>
                  {g.periods.map((p) => {
                    const existing = (t.scores || []).find((s) => s.period_label === p.label);
                    return (
                      <td key={p.label} className="px-2 py-3 text-center align-middle">
                        <span
                          title={existing?.notes || (existing?.score != null ? 'Scored' : 'Not scored yet')}
                          className={`inline-flex items-center justify-center w-12 h-9 rounded-md border text-sm font-semibold ${scoreColor(existing?.score)}`}>
                          {existing?.score != null ? Number(existing.score).toString() : '–'}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export default function MyGoalScorecardPage() {
  const { user } = useAuth();
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    api.get(`/goals/staff/${user.id}/matrix`)
      .then(({ data }) => setMatrix(data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load your scorecard.'))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const categories = matrix?.categories || [];
  const avgDisplay = matrix?.avg_score == null ? '—' : Number(matrix.avg_score).toFixed(2);
  const tier = matrix?.tier;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-4 animate-slide-up">
      <div className="flex items-center gap-2">
        <TrophyIcon className="h-7 w-7 text-orange-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Scorecard</h1>
          <p className="text-sm text-gray-500">Your goal ratings by period. Scores are set by your manager.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{user?.first_name} {user?.last_name}</h2>
                <p className="text-sm text-gray-500">{ROLE_LABEL[user?.role] || user?.role}</p>
              </div>
              <div className="flex items-stretch gap-3">
                <div className="bg-gray-50 rounded-lg px-4 py-2 text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Avg Score</p>
                  <p className="text-2xl font-bold text-gray-900">{avgDisplay}</p>
                  <p className="text-[11px] text-gray-400">{matrix?.total_scored || 0} scored</p>
                </div>
                {tier ? (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2 text-center min-w-[160px]">
                    <p className="text-xs text-orange-600 uppercase tracking-wide">Comp Tier</p>
                    <p className="text-2xl font-bold text-orange-700">{Number(tier.raise_percent).toFixed(1)}%</p>
                    <p className="text-[11px] text-orange-600">{tier.label}</p>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-dashed border-gray-200 rounded-lg px-4 py-2 text-center min-w-[160px]">
                    <p className="text-xs text-gray-400 uppercase tracking-wide">Comp Tier</p>
                    <p className="text-sm text-gray-400 mt-2">No scores yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {categories.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
              <ChartBarIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No goal scorecard has been set up for your role yet.</p>
            </div>
          ) : (
            categories.map((cat) => <CategoryBlock key={cat.name} cat={cat} />)
          )}
        </>
      )}
    </div>
  );
}
